import {
    Box,
    Chip,
    Collapse,
    Grid2,
    IconButton,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    useGetOrders,
    manuallyTriggerRollback,
    addMoreRollbackTime as addMoreRollbackTimeApi,
} from '@openapi/orders/orders';
import { useGetBookingItems } from '@openapi/booking-items/booking-items';
import { simpleRefundBooking } from '@openapi/bookings/bookings';
import useNotify from '../../../../hooks/useNotify';
import BlockWithTitle from '../../../../components/atom/BlockWithTitle';
import SortableTableCell from '../../../../components/atom/SortableTableCell';
import SkeletonTable from '../../../../components/atom/SkeletonTable';
import useSorting from '../../../../hooks/useSorting';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { formatDateTimeInVenueTz } from '../../../../helpers/formatDateTime.ts';
import { MODAL_TYPE, useModals } from '../../../../hooks/useModal.jsx';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { useAuth } from '../../../../hooks/useAuth';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import NoteIcon from '@mui/icons-material/Note';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaymentsIcon from '@mui/icons-material/Payments';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { ORDER_STATUSES } from '../../../../helpers/constants.js';
import RefundChunksTable from './RefundChunksTable.jsx';

function renderRollbackCountdown(order, operatingId) {
    if (order.status !== ORDER_STATUSES.PENDING || operatingId === order.id) return null;
    const diffMs = new Date(order.rollbackTimeAt) - new Date();
    if (diffMs <= 0) return null;
    const totalMins = Math.floor(diffMs / 60000);
    return (
        <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }} color="success.main">
            +{Math.floor(totalMins / 60)}h {totalMins % 60}m
        </Typography>
    );
}

// Styles for the hierarchical table
const orderRowStyles = {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    '&:hover': {
        backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '& > td': {
        borderBottom: '1px solid rgba(224, 224, 224, 0.7)',
    },
};

const transactionRowStyles = {
    backgroundColor: 'transparent',
    '& td': {
        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
        py: 0.75,
    },
    '&:last-child td': {
        borderBottom: 'none',
    },
};

const OrdersDetails = ({
    bookingId,
    reservationHash,
    paymentMethods,
    venueAllowSplitPayment,
    reloadBookingData,
    reloadParentOrders,
    limitedModification,
    collapsible,
    absorbProcessingFee,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();
    const { sort, sortDir, setSorting } = useSorting(['id', 'DESC']);
    const { showModal } = useModals();
    const { can, CAPABILITIES } = useCapabilities();
    const { user } = useAuth();
    const isSuperAdmin = !!user?.isSuperAdmin;
    const [operatingId, setOperatingId] = useState(null);
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const canCustomRollback = can(CAPABILITIES.BOOKINGS_CUSTOM_ROLLBACK_TIMES);
    const canRefundBookings = can(CAPABILITIES.BOOKINGS_REFUND);

    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetOrders(
        {
            optionalId: Number(bookingId),
            pageSize: 100,
            sort,
            sortDir,
            includeTransactions: true,
        },
        {
            query: {
                onError: (err) => {
                    notifyError(err);
                    navigate('/bookings');
                },
            },
        },
    );

    const { data: bookingItemsData } = useGetBookingItems(
        { optionalId: Number(bookingId), pageSize: 10000 },
        { query: { onError: notifyError } },
    );

    const bookingItemsMap = useMemo(() => {
        const map = new Map();
        (bookingItemsData?.bookingItems || []).forEach((item) => map.set(item.id, item));
        return map;
    }, [bookingItemsData]);

    const ticketSummary = useMemo(() => {
        const items = bookingItemsData?.bookingItems || [];
        if (!items.length) return null;
        const typeCounts = {};
        let seated = 0;
        let unseated = 0;
        items.forEach((item) => {
            const name = item.ticketTypeName || '?';
            typeCounts[name] = (typeCounts[name] || 0) + 1;
            if (item.ticketSeatId || item.ticketSeatName) seated++;
            else unseated++;
        });
        return { typeCounts, seated, unseated, total: items.length };
    }, [bookingItemsData]);

    const getOrderTicketIds = (order) => [
        ...(order.ticketsAdded || []),
        ...(order.ticketsChanged || []),
        ...(order.ticketsRemoved || []),
    ];

    const toggleOrderExpand = (orderId) => {
        setExpandedOrders((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const triggerManuallyRollback = useCallback(
        async (id) => {
            setOperatingId(id);
            try {
                // Empty body — axios strips Content-Type when data is
                // undefined and the backend validator returns HTTP 415.
                await manuallyTriggerRollback(id, {});
                notifySuccess(t('orders.notifications.rollbacked'));
                reloadData();
                reloadBookingData();
                reloadParentOrders?.();
            } catch (_error) {
                notifyError(t('orders.notifications.rollbackError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, reloadBookingData, reloadParentOrders, t],
    );

    const addMoreRollbackTime = useCallback(
        async (id) => {
            setOperatingId(id);
            try {
                await addMoreRollbackTimeApi(id, {});
                notifySuccess(t('orders.notifications.moreTimeAdded'));
                reloadData();
            } catch (_error) {
                notifyError(t('orders.notifications.moreTimeError'));
            } finally {
                setOperatingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    // Outstanding refund amount across all REFUNDING orders (gross + fee
    // remaining after existing transactions). When this is 0 the parent
    // order is already covered by transactions and just waiting on the
    // REFUNDING → REFUNDED status transition — rendering the block only
    // when there's still money to return keeps the title from lingering
    // over an empty table between the successful refund call and the
    // status propagation.
    const refundingOutstanding = (data?.orders || []).reduce((sum, order) => {
        if (order.status !== ORDER_STATUSES.REFUNDING) return sum;
        const txSum = (order.transactions || []).reduce((acc, tx) => acc + (tx.amount || 0), 0);
        return sum + (order.amount * -1 + txSum);
    }, 0);
    const hasRefundingOrders = refundingOutstanding > 0;

    // processing_fee is a signed ledger: payments stamp +fee, fee-returning
    // refunds stamp -fee, everything else is 0. Summing finished rows gives
    // the net fee still held by the venue.
    const totalProcessingFees =
        data?.orders?.reduce((sum, order) => {
            const orderProcessingFees = order.transactions?.reduce(
                (txSum, tx) => (tx.finished ? txSum + (tx.processingFee || 0) : txSum),
                0,
            );
            return sum + (orderProcessingFees || 0);
        }, 0) || 0;

    // Aggregate finished signed processing fees from an order's transactions
    // so a refund order reads "-$100 / Total: -$103.30" instead of leaving the
    // fee visible only on the txn row below. Unfinished txns are excluded —
    // they may still roll back.
    const orderFeeSumOf = (order) =>
        (order.transactions || []).reduce(
            (sum, tx) => (tx.finished ? sum + (tx.processingFee || 0) : sum),
            0,
        );

    // Render order row
    const renderOrderRow = (order) => {
        const ticketIds = getOrderTicketIds(order);
        const hasTickets = ticketIds.length > 0;
        const isExpanded = expandedOrders.has(order.id);
        const isRefunded =
            order.status === ORDER_STATUSES.REFUNDED || order.status === ORDER_STATUSES.REFUNDING;
        const rowSx = isRefunded
            ? {
                  ...orderRowStyles,
                  backgroundColor: 'rgba(211, 47, 47, 0.05)',
                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.10)' },
              }
            : orderRowStyles;
        return (
            <TableRow key={`order-${order.id}`} sx={rowSx}>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            disabled={!hasTickets}
                            onClick={() => toggleOrderExpand(order.id)}
                            sx={{ opacity: hasTickets ? 1 : 0.2, p: 0.25 }}
                        >
                            {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                            ) : (
                                <ExpandMoreIcon fontSize="small" />
                            )}
                        </IconButton>
                        <ReceiptIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight={600}>
                            #{order.id}
                        </Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            {formatCentsToUsd(order.amount)}
                        </Typography>
                        {orderFeeSumOf(order) !== 0 && (
                            <>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', fontSize: '0.65rem' }}
                                >
                                    {orderFeeSumOf(order) > 0 ? '+ ' : ''}
                                    {formatCentsToUsd(orderFeeSumOf(order))} {t('transactions.fee')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.primary"
                                    sx={{
                                        display: 'block',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {t('transactions.columns.total')}:{' '}
                                    {formatCentsToUsd(
                                        order.amount -
                                            (order.discountAmount || 0) +
                                            orderFeeSumOf(order),
                                    )}
                                </Typography>
                            </>
                        )}
                        {order.discountAmount > 0 && (
                            <Typography
                                variant="caption"
                                color="success.main"
                                sx={{ display: 'block', fontSize: '0.65rem' }}
                            >
                                {t('email.parts.cart.discountLabel')}:
                                {order.couponCode ? ` (${order.couponCode})` : ''} -
                                {formatCentsToUsd(order.discountAmount)}
                            </Typography>
                        )}
                    </Box>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        -
                    </Typography>
                </TableCell>
                <TableCell>{formatDateTimeInVenueTz(order.createdAt)}</TableCell>
                <TableCell>
                    {order.rollbackTimeAt ? (
                        <Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                            >
                                {formatDateTimeInVenueTz(order.rollbackTimeAt)} HST
                            </Typography>
                            {renderRollbackCountdown(order, operatingId)}
                        </Box>
                    ) : (
                        '-'
                    )}
                </TableCell>
                <TableCell align="right">
                    {!limitedModification && order.amount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {order.status === ORDER_STATUSES.PENDING ? (
                                <>
                                    <Tooltip title={t('orders.addMoreTime')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={operatingId === order.id}
                                                onClick={() => {
                                                    if (canCustomRollback) {
                                                        showModal(MODAL_TYPE.CUSTOM_ROLLBACK_TIME, {
                                                            orderId: order.id,
                                                            currentRollbackTimeAt:
                                                                order.rollbackTimeAt,
                                                            onSuccess: () => {
                                                                reloadData();
                                                            },
                                                        });
                                                    } else {
                                                        showModal(MODAL_TYPE.CONFIRMATION, {
                                                            title: t('confirmationModal.title'),
                                                            content: t('confirmationModal.content'),
                                                            confirm: () =>
                                                                addMoreRollbackTime(order.id),
                                                        });
                                                    }
                                                }}
                                            >
                                                <MoreTimeIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t('common.rollback')}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={operatingId === order.id}
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: t('confirmationModal.title'),
                                                        content: t('confirmationModal.content'),
                                                        confirm: () =>
                                                            triggerManuallyRollback(order.id),
                                                    });
                                                }}
                                            >
                                                <RotateLeftIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t('common.pay')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                showModal(MODAL_TYPE.PAYMENT, {
                                                    title: t('paymentModal.title'),
                                                    content: t('paymentModal.content'),
                                                    order: order,
                                                    allOrders: data?.orders || [],
                                                    paymentMethods,
                                                    reservationId: bookingId,
                                                    reservationHash,
                                                    reloadData: () => {
                                                        reloadData();
                                                        reloadParentOrders?.();
                                                    },
                                                    venueAllowSplitPayment,
                                                    reloadBookingData,
                                                    absorbProcessingFee,
                                                });
                                            }}
                                        >
                                            <AttachMoneyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                order.status === ORDER_STATUSES.SETTLED &&
                                canRefundBookings && (
                                    <Tooltip title={t('common.refundReservation')}>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                showModal(MODAL_TYPE.REFUND_CONFIRMATION, {
                                                    title: t('refundModal.title'),
                                                    content: t('refundModal.content'),
                                                    bookingId: Number(bookingId),
                                                    defaultAmount: order.amount,
                                                    confirm: async (id, amount) => {
                                                        try {
                                                            await simpleRefundBooking(id, {
                                                                amount,
                                                            });
                                                            reloadData();
                                                            reloadBookingData();
                                                            reloadParentOrders?.();
                                                        } catch (err) {
                                                            notifyError(err);
                                                        }
                                                    },
                                                });
                                            }}
                                        >
                                            <MoneyOffIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )
                            )}
                        </Box>
                    )}
                </TableCell>
                <TableCell align="right">
                    <Chip
                        label={t(`orderstatus.${order.status}`)}
                        size="small"
                        color={
                            order.status === ORDER_STATUSES.SETTLED
                                ? 'success'
                                : order.status === ORDER_STATUSES.PENDING
                                  ? 'warning'
                                  : isRefunded
                                    ? 'error'
                                    : 'default'
                        }
                    />
                </TableCell>
            </TableRow>
        );
    };

    const renderTicketExpansion = (order) => {
        const ticketIds = getOrderTicketIds(order);
        if (ticketIds.length === 0) return null;
        // Only render while open - a collapsed Collapse still leaves an empty
        // strip between rows that reads as an oversized divider.
        if (!expandedOrders.has(order.id)) return null;
        const removedIds = new Set(order.ticketsRemoved || []);
        return (
            <TableRow key={`order-tickets-${order.id}`}>
                <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none', p: 0 }}>
                    <Collapse in appear unmountOnExit>
                        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '13%' }} />
                                <col style={{ width: '67%' }} />
                            </colgroup>
                            <TableBody>
                                {ticketIds.map((ticketId) => {
                                    const item = bookingItemsMap.get(ticketId);
                                    const isRemoved = removedIds.has(ticketId);
                                    const isSeated = !!(item?.ticketSeatId || item?.ticketSeatName);
                                    return (
                                        <TableRow
                                            key={ticketId}
                                            sx={{
                                                '& td': { borderBottom: 'none', py: 0.5 },
                                            }}
                                        >
                                            {/* col 1 — icon + option (bold) + type */}
                                            <TableCell sx={{ pl: 2 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                    }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            isSeated
                                                                ? t('bookingitems.seated')
                                                                : t('bookingitems.unseated')
                                                        }
                                                    >
                                                        {isSeated ? (
                                                            <FiberManualRecordIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: 'success.main',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: 'text.disabled',
                                                                    flexShrink: 0,
                                                                }}
                                                            />
                                                        )}
                                                    </Tooltip>
                                                    {item ? (
                                                        <>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight={700}
                                                            >
                                                                {item.ticketOptionName}
                                                            </Typography>
                                                            {item.ticketTypeName && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                >
                                                                    {item.ticketTypeName}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.disabled"
                                                            sx={{ fontStyle: 'italic' }}
                                                        >
                                                            #{ticketId}
                                                        </Typography>
                                                    )}
                                                    {isRemoved && (
                                                        <Chip
                                                            label={t('common.removed')}
                                                            size="small"
                                                            color="error"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            {/* col 2 — price */}
                                            <TableCell>
                                                {item ? (
                                                    <Typography variant="body2" fontWeight={500}>
                                                        {formatCentsToUsd(item.price)}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            {/* col 3 — remaining columns */}
                                            <TableCell />
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Collapse>
                </TableCell>
            </TableRow>
        );
    };

    // Render transaction row
    const renderTransactionRow = (transaction) => {
        const noteText = transaction.paymentInfo?.note || transaction.refundNote || null;
        return (
            <React.Fragment key={`tx-${transaction.id}`}>
                <TableRow sx={transactionRowStyles}>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: 2 }}>
                            <SubdirectoryArrowRightIcon
                                fontSize="small"
                                sx={{ color: 'text.disabled', mr: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                TX #{transaction.id}
                            </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box>
                            <Typography variant="body2">
                                {formatCentsToUsd(transaction.amount)}
                            </Typography>
                            {transaction.processingFee !== 0 && (
                                // Refund rows have signed-negative processingFee
                                // (Stripe also pulled the fee back). `formatCentsToUsd`
                                // already prints the sign — prefix `+` only on
                                // payments so refund rows read as e.g. "-$18.56 fee".
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', fontSize: '0.65rem' }}
                                >
                                    {transaction.processingFee > 0 ? '+ ' : ''}
                                    {formatCentsToUsd(transaction.processingFee)}{' '}
                                    {t('transactions.fee')}
                                </Typography>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                            {(() => {
                                if (
                                    transaction.paymentType === 'coupon' &&
                                    transaction.paymentInfo?.couponCode
                                ) {
                                    return `${transaction.paymentType} (${transaction.paymentInfo.couponCode})`;
                                }
                                const last4 =
                                    transaction.paymentInfo?.card?.last4 ||
                                    transaction.paymentInfo?.stripeResponse?.payment_method_details
                                        ?.card?.last4;
                                const brand =
                                    transaction.paymentInfo?.card?.brand ||
                                    transaction.paymentInfo?.stripeResponse?.payment_method_details
                                        ?.card?.brand;
                                if (last4) {
                                    const label = brand
                                        ? `${transaction.paymentType} ${brand} ****${last4}`
                                        : `${transaction.paymentType} ****${last4}`;
                                    return label;
                                }
                                return transaction.paymentType || '-';
                            })()}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2" color="text.secondary">
                            {formatDateTimeInVenueTz(transaction.createdAt)}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {transaction.processingFee !== 0 && (
                                // Stripe-refund rows carry the returned fee on the
                                // same row's signed processingFee, so ticket+fee adds
                                // to the full charged/refunded amount the merchant
                                // sees on the Stripe dashboard.
                                <Typography variant="caption" color="text.secondary">
                                    {t('transactions.columns.total')}:{' '}
                                    <strong>
                                        {formatCentsToUsd(
                                            transaction.amount + (transaction.processingFee || 0),
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            <Tooltip title={t('transactions.editNote')}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        showModal(MODAL_TYPE.TRANSACTION_NOTE_EDIT, {
                                            transaction,
                                            onSuccess: reloadBookingData,
                                        })
                                    }
                                >
                                    <NoteIcon
                                        fontSize="small"
                                        color={
                                            transaction.paymentInfo?.note ? 'primary' : 'inherit'
                                        }
                                    />
                                </IconButton>
                            </Tooltip>
                            {can(CAPABILITIES.BOOKINGS_RE_SOURCE) && (
                                <Tooltip title={t('transactions.changePaymentType.button')}>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            showModal(MODAL_TYPE.TRANSACTION_PAYMENT_TYPE, {
                                                transaction,
                                                paymentMethods,
                                                onSuccess: reloadBookingData,
                                            })
                                        }
                                    >
                                        <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            {isSuperAdmin && (
                                <Tooltip title={t('transactions.editProcessingFee')}>
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            showModal(MODAL_TYPE.TRANSACTION_PROCESSING_FEE_EDIT, {
                                                transaction,
                                                onSuccess: reloadBookingData,
                                            })
                                        }
                                    >
                                        <PaymentsIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                        <Chip
                            label={
                                transaction.finished
                                    ? t('transactions.status.completed')
                                    : t('transactions.status.processing')
                            }
                            size="small"
                            color={transaction.finished ? 'success' : 'warning'}
                            variant="outlined"
                        />
                    </TableCell>
                </TableRow>
                {noteText && (
                    <TableRow sx={transactionRowStyles}>
                        <TableCell />
                        <TableCell colSpan={6} sx={{ pt: 0, pb: 1 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}
                            >
                                {t('transactions.noteLabel')}: {noteText}
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    };

    return (
        <Grid2 size={{ xs: 12 }} spacing={2} container>
            {hasRefundingOrders && canRefundBookings && (
                <Grid2 size={{ xs: 12 }}>
                    <BlockWithTitle title={t('refundChunks.title')}>
                        <RefundChunksTable
                            orders={data.orders}
                            bookingId={bookingId}
                            reloadData={reloadData}
                            reservationHash={reservationHash}
                        />
                    </BlockWithTitle>
                </Grid2>
            )}
            <Grid2 size={{ xs: 12 }}>
                <BlockWithTitle title={t('orders.titleWithTickets')} collapsible={collapsible}>
                    {ticketSummary && (
                        <Grid2 size={{ xs: 12 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                    mt: -1,
                                }}
                            >
                                {Object.entries(ticketSummary.typeCounts).map(([name, count]) => (
                                    <Typography key={name} variant="caption" color="text.secondary">
                                        {name} ×{count}
                                    </Typography>
                                ))}
                                <Typography variant="caption" color="text.disabled">
                                    |
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ticketSummary.seated} {t('bookingitems.seated').toLowerCase()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {ticketSummary.unseated}{' '}
                                    {t('bookingitems.unseated').toLowerCase()}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    ({t('orders.ticketTotal', { total: ticketSummary.total })})
                                </Typography>
                            </Box>
                        </Grid2>
                    )}
                    <TableContainer>
                        <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 600 }}>
                            <TableHead>
                                <TableRow>
                                    <SortableTableCell
                                        arrow={sort === 'id' ? sortDir : ''}
                                        onClick={() => setSorting('id')}
                                        sx={{ width: '20%' }}
                                    >
                                        {t('orders.columns.id')}
                                    </SortableTableCell>
                                    <SortableTableCell
                                        arrow={sort === 'amount' ? sortDir : ''}
                                        onClick={() => setSorting('amount')}
                                        sx={{ width: '13%' }}
                                    >
                                        {t('orders.columns.amount')}
                                    </SortableTableCell>
                                    <TableCell sx={{ width: '12%' }}>
                                        {t('transactions.columns.payment_type')}
                                    </TableCell>
                                    <SortableTableCell
                                        arrow={sort === 'createdAt' ? sortDir : ''}
                                        onClick={() => setSorting('createdAt')}
                                        sx={{ width: '16%' }}
                                    >
                                        {t('orders.columns.createdAt')}
                                    </SortableTableCell>
                                    <SortableTableCell
                                        arrow={sort === 'rollbackTimeAt' ? sortDir : ''}
                                        onClick={() => setSorting('rollbackTimeAt')}
                                        sx={{ width: '17%' }}
                                    >
                                        {t('orders.columns.rollbackTimeAt')}
                                    </SortableTableCell>
                                    <TableCell sx={{ width: '10%' }} align="right">
                                        {t('common.actions')}
                                    </TableCell>
                                    <SortableTableCell
                                        arrow={sort === 'status' ? sortDir : ''}
                                        onClick={() => setSorting('status')}
                                        sx={{ width: '12%' }}
                                        justifyContent="flex-end"
                                    >
                                        {t('orders.columns.status')}
                                    </SortableTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <SkeletonTable rows={4} cells={7} />
                                ) : (
                                    data?.orders?.map((order) => (
                                        <React.Fragment key={order.id}>
                                            {renderOrderRow(order)}
                                            {renderTicketExpansion(order)}
                                            {order.transactions?.map((tx) =>
                                                renderTransactionRow(tx),
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {totalProcessingFees > 0 && (
                        <Box sx={{ mt: 2, textAlign: 'right' }}>
                            <Typography variant="subtitle2" color="text.secondary">
                                <b>{t('transactions.totalProcessingFees')}:</b>{' '}
                                {formatCentsToUsd(totalProcessingFees)}
                            </Typography>
                        </Box>
                    )}
                </BlockWithTitle>
            </Grid2>
        </Grid2>
    );
};

export default OrdersDetails;
